import { ErrorMessage } from "@hookform/error-message";
import * as moment from "moment";
import React, { useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import ReactSelect from "react-select";
import { toast } from "react-toastify";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Modal,
  ModalBody,
  UncontrolledDropdown,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Icon,
  Row,
  RSelect,
} from "../../components/Component";
import DatePicker from "../../components/range-date-picker/RangeDatePicker";
import Table from "../../components/react-table/Table";
import { DELETE, GET, POST, PUT } from "../../helper/api";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { ACTION_SCAN, SHIFT, TIME_KEEPING_IS_LOCKED_TYPE, TIME_KEEPING_LEAVE_TYPE, USER_ROLES } from "../../utils/Utils";

import { Buffer } from "buffer";
import QRCode from "react-qr-code";
import { useHistory, useLocation } from "react-router-dom";
import { useUser } from '../../context/user';
import { TimeKeepingHoliday } from "./TimeKeepingHoliday";

const SHIFT_OPTIONS = Object.keys(SHIFT).map((key) => ({
  value: key,
  label: SHIFT[key],
}));

const TIME_KEEPING_IS_LOCKED_TYPE_OPTIONS = Object.keys(TIME_KEEPING_IS_LOCKED_TYPE).map((key) => ({
  value: key,
  label: TIME_KEEPING_IS_LOCKED_TYPE[key],
}));

const TIME_KEEPING_LEAVE_TYPE_OPTIONS = Object.keys(TIME_KEEPING_LEAVE_TYPE).map((key) => ({
  value: key,
  label: TIME_KEEPING_LEAVE_TYPE[key],
}));

const DEFAULT_TIME_KEEPING = {
  id: "",
  date: moment().format("YYYY-MM-DD"),
  userId: "",
  shift: "",
  totalWorkingHour: 8,
  leaveType: null,
  isOilRefining: false,
  isRunMachine: false,
  isOffHoliday: false,
  isAllowMultiple: false,
  isViolationOfCleanliness: false,
};

const TimeKeepingManager = () => {
  const {
    state: {
      user,
    },
  } = useUser();
  const isHavePermissionLockTimeKeeping = [USER_ROLES.MANAGER, USER_ROLES.ADMIN, USER_ROLES.ENTRY_STAFF].includes(user?.role);
  const history = useHistory();
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({});
  const [view, setView] = useState({
    edit: false,
    add: false,
    isDelete: false,
    isScan: false,
    isLock: false,
    isAddTimeKeepingInHoliday: false,
  });
  const {
    errors,
    register,
    handleSubmit,
    reset,
    getValues,
    control,
    setValue,
    watch,
  } = useForm({
    defaultValues: DEFAULT_TIME_KEEPING,
  });

  const leaveType = useWatch({
    control,
    name: "leaveType",
  });

  const isOffHoliday = useWatch({
    control,
    name: "isOffHoliday",
  });

  const isOilRefining = watch("isOilRefining");
  const isRunMachine = watch("isRunMachine");

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [loading, setLoading] = useState(false);
  const [editId, setEditedId] = useState();
  const [scanData, setScanData] = useState();
  const [lockIds, setLockIds] = useState();
  const [deleteId, setDeleteId] = useState();
  const [timeNow, setTimeNow] = useState();
  const [smOption, setSmOption] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [filter, setFilter] = useState({
    userId: searchParams.get("userId") || undefined,
    shift: searchParams.get("shift") || undefined,
    isLocked: searchParams.get("isLocked") || undefined,
  });

  const [rangeDate, setRangeDate] = useState({
    startDate: searchParams.get("startDate")
      ? moment(searchParams.get("startDate")).startOf("days").toDate()
      : moment().startOf("days").toDate(),
    endDate: searchParams.get("endDate")
      ? moment(searchParams.get("endDate")).endOf("days").toDate()
      : moment().endOf("days").toDate(),
  });

  const onRangeChange = (dates) => {
    const [startDate, endDate] = dates;
    setRangeDate({
      startDate: startDate
        ? moment(startDate)?.startOf("days").toDate()
        : startDate,
      endDate: endDate ? moment(endDate)?.endOf("days").toDate() : endDate,
    });
    if (startDate && endDate) {
      setTimeNow(moment().valueOf());
    }
  };

  const colTable = [
    {
      Header: "Ngày",
      accessor: (item) => moment(item.date).format("DD/MM/YYYY"),
    },
    {
      Header: "Nhân viên",
      accessor: (item) => item?.user?.name || "",
    },

    {
      Header: "Ca làm",
      accessor: (item) => SHIFT[item.shift] || "",
    },
    {
      Header: "Nghỉ phép",
      className: 'text-right',
      accessor: (item) => item.leaveType ? TIME_KEEPING_LEAVE_TYPE[item.leaveType] : '',
    },
    {
      Header: "Giờ làm",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        displayType={"text"}
        value={item.workingHour || 0}
        thousandsGroupStyle="thousand"
        thousandSeparator=","
        decimalScale={2}
      />,
    },
    {
      Header: "Giờ làm thêm",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        displayType={"text"}
        value={item.extraHour || 0}
        thousandsGroupStyle="thousand"
        thousandSeparator=","
        decimalScale={2}
      />,
    },
    {
      Header: "Giờ thưởng thêm",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        displayType={"text"}
        value={item.bonusHour || 0}
        thousandsGroupStyle="thousand"
        thousandSeparator=","
        decimalScale={2}
      />,
    },
    {
      Header: "Tổng giờ",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        displayType={"text"}
        value={item.calculatedHour || 0}
        thousandsGroupStyle="thousand"
        thousandSeparator=","
        decimalScale={2}
      />,
    },
    {
      Header: "Mất vệ sinh",
      className: 'text-right',
      accessor: (item) => item.isViolationOfCleanliness ? 'Bị phạt' : '',
    },
    {
      Header: "Trợ cấp ăn(đ)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        displayType={"text"}
        value={item.foodAllowance || 0}
        thousandsGroupStyle="thousand"
        thousandSeparator=","
        decimalScale={2}
      />,
    },
    {
      Header: "Trợ cấp ăn tăng ca(đ)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        displayType={"text"}
        value={item.extraHourFoodAllowance || 0}
        thousandsGroupStyle="thousand"
        thousandSeparator=","
        decimalScale={2}
      />,
    },
    {
      Header: "Trợ cấp nấu dầu(đ)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        displayType={"text"}
        value={item.oilAllowance || 0}
        thousandsGroupStyle="thousand"
        thousandSeparator=","
        decimalScale={2}
      />,
    },
    {
      Header: "Xác nhận",
      accessor: (item) =>
        item.agreedBy ? (
          "Đã xác nhận"
        ) : (
          <Button
            className={"btn btn-primary"}
            onClick={() => {
              setScanData(item)
              toggle('isScan')
            }}
          >
            Hiện Scan
          </Button>
        ),
    },
    ...(isHavePermissionLockTimeKeeping ? [
      {
        Header: "Khóa",
        accessor: (item) =>
          item.isLocked ? (
            "Đã khóa"
          ) : (
            <Button
              className={"btn btn-primary"}
              onClick={() => {
                setLockIds([item.id])
                toggle('isLock')
              }}
            >
              Khóa
            </Button>
          ),
      },
    ] : []),
    {
      Header: "Hành động",
      accessor: (item) => (
        <ul className="nk-tb-actions gx-1">
          <li>
            <UncontrolledDropdown>
              <DropdownToggle
                tag="a"
                className="btn btn-icon dropdown-toggle btn-trigger"
              >
                <Icon name="more-h"></Icon>
              </DropdownToggle>
              <DropdownMenu right>
                <ul className="link-list-opt no-bdr">
                  <li>
                    <DropdownItem
                      tag="a"
                      href="#dropdown"
                      onClick={(ev) => {
                        ev.preventDefault();
                        onFormCancel();
                        onEditClick(item.id);
                        toggle("edit");
                      }}
                    >
                      <Icon name="edit"></Icon>
                      <span>Sửa</span>
                    </DropdownItem>
                  </li>
                  <li>
                    <DropdownItem
                      tag="a"
                      href="#dropdown"
                      onClick={(ev) => {
                        ev.preventDefault();
                        onFormCancel();
                        setDeleteId(item.id);
                        toggle("isDelete");
                      }}
                    >
                      <Icon name="trash" className="text-danger"></Icon>
                      <span>Xoá</span>
                    </DropdownItem>
                  </li>
                </ul>
              </DropdownMenu>
            </UncontrolledDropdown>
          </li>
        </ul>
      ),
    },
  ];


  const genScanQRData = (item) => {
    if (!item) {
      return '';
    }
    const data = JSON.stringify({
      action: ACTION_SCAN.TIME_KEEPING,
      data: {
        id: item.id
      }
    });

    return Buffer.from(data).toString("base64");;
  }

  useEffect(() => {
    GET("/user/list-workers", {}, true).then((resp) => {
      setUserOptions(
        resp.data?.map((c) => ({
          label: c.name,
          value: c.id,
          data: c,
        }))
      );
    });
  }, []);

  // Changing state value when searching name
  useEffect(() => {
    const newParams = new URLSearchParams({
      ...rangeDate,
      ...(filter.userId ? { userId: filter.userId } : {}),
      ...(filter.shift ? { shift: filter.shift } : {}),
      ...(filter.isLocked !== undefined ? { isLocked: filter.isLocked } : {}),
    });
    history.replace({
      pathname: location.pathname,
      search: newParams.toString(),
    });

    setLoading(true);
    let params = {
      startDate: moment(rangeDate.startDate).format("YYYY-MM-DD"),
      endDate: moment(rangeDate.endDate).format("YYYY-MM-DD"),
      ...(filter.userId ? { userId: filter.userId } : {}),
      ...(filter.shift ? { shift: filter.shift } : {}),
      ...(filter.isLocked !== undefined ? { isLocked: filter.isLocked } : {}),
    };

    GET(`employee/keepingtime`, params, true)
      .then(({ data }) => {
        setData([...data?.rows]);
        setSummary({ ...data?.summary });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [timeNow, filter]);

  // toggle function to view order details
  const toggle = (type) => {
    setView({
      ...view,
      [type]: !view[type],
    });
  };

  const onEditClick = (id) => {
    setLoading(true);
    setEditedId(id);

    GET(`employee/keepingtime/${id}`, {}, true)
      .then(({ data }) => {
        reset({
          ...data,
          totalWorkingHour: data.workingHour + data.extraHour,
        });
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại."
        );
        onFormCancel();

      })
      .finally(() => {
        setLoading(false);
      });
  };

  const resetData = () => {
    onFormCancel();
    setTimeNow(moment().valueOf());
  };

  // Submits form data
  const onHandleSubmit = () => {
    setLoading(true);
    const {
      date,
      userId,
      shift,
      totalWorkingHour,
      isOilRefining,
      isRunMachine,
      isOffHoliday,
      isAllowMultiple,
      isViolationOfCleanliness,
      leaveType
    } = getValues();

    const formData = {
      date,
      userId,
      shift,
      totalWorkingHour: +totalWorkingHour,
      isOilRefining,
      isRunMachine,
      isOffHoliday,
      isAllowMultiple,
      isViolationOfCleanliness,
      leaveType
    };

    const request = editId
      ? PUT(`employee/keepingtime/${editId}`, formData, {}, true)
      : POST("employee/keepingtime", formData, {}, true);

    request
      .then((res) => {
        if (editId) {
          toast.success("Cập nhật thành công");
        } else {
          toast.success("Thêm thành công");
        }
        resetData();
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // function to close the form modal
  const onFormCancel = () => {
    setView({ add: false, edit: false, isDelete: false, isScan: false, isAddTimeKeepingInHoliday: false });
    reset(DEFAULT_TIME_KEEPING);
    setEditedId();
    setScanData()
    setLockIds()
    setDeleteId();
  };

  const deleteOrder = (id) => {
    if (!id) {
      return;
    }
    DELETE(`employee/keepingtime/${id}`, {}, true)
      .then((res) => {
        toast.success("Xóa chấm công thành công");
        resetData();
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại."
        );
      });
  };

  const confirmLocked = () => {
    if (lockIds.length === 0) {
      return;
    }
    POST(`/employee/keepingtime/lock-time-keeping`, {
      ids: lockIds
    }, {}, true)
      .then((res) => {
        toast.success("Khóa chấm công thành công");
        resetData();
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại."
        );
      });
  }
  return (
    <React.Fragment>
      <Head title="Quản lý chấm công"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Chấm công</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <a
                  href="#more"
                  className="btn btn-icon btn-trigger toggle-expand mr-n1"
                  onClick={(ev) => {
                    ev.preventDefault();
                    setSmOption(!smOption);
                  }}
                >
                  <Icon name="more-v"></Icon>
                </a>
                <div
                  className="toggle-expand-content"
                  style={{ display: smOption ? "block" : "none" }}
                >
                  <ul className="d-lg-flex g-3 pt-1 pb-1 custom-filter-header justify-content-end">
                    <li>
                      <div className="form-control-wrap">
                        <DatePicker
                          selected={rangeDate.startDate}
                          startDate={rangeDate.startDate}
                          onChange={onRangeChange}
                          endDate={rangeDate.endDate}
                          maxDate={new Date()}
                          selectsRange
                          className="form-control date-picker"
                        />
                      </div>
                    </li>

                    <li>
                      <div
                        className="form-control-wrap"
                        style={{ width: "200px" }}
                      >
                        <ReactSelect
                          isClearable
                          placeholder="Tìm kiếm ca làm"
                          classNamePrefix="form-control"
                          options={SHIFT_OPTIONS}
                          value={SHIFT_OPTIONS.find(
                            (c) => c.value === filter.shift
                          )}
                          onChange={(val) =>
                            setFilter({
                              ...filter,
                              shift: (val && val.value) || "",
                            })
                          }
                        />
                      </div>
                    </li>
                    <li>
                      <div
                        className="form-control-wrap"
                        style={{ width: "200px" }}
                      >
                        <ReactSelect
                          isClearable
                          placeholder="Lọc khóa chấm công"
                          classNamePrefix="form-control"
                          options={TIME_KEEPING_IS_LOCKED_TYPE_OPTIONS}
                          value={TIME_KEEPING_IS_LOCKED_TYPE_OPTIONS.find(
                            (c) => c.value === filter.isLocked
                          )}
                          onChange={(val) =>
                            setFilter({
                              ...filter,
                              isLocked: (val && val.value) || undefined,
                            })
                          }
                        />
                      </div>
                    </li>

                    <li>
                      <div
                        className="form-control-wrap"
                        style={{ width: "200px" }}
                      >
                        <ReactSelect
                          isClearable
                          placeholder="Tìm kiếm nhân viên"
                          classNamePrefix="form-control"
                          options={userOptions}
                          value={userOptions.find(
                            (c) => c.value === +filter.userId
                          )}
                          onChange={(val) =>
                            setFilter({
                              ...filter,
                              userId: (val && val.value) || "",
                            })
                          }
                        />
                      </div>
                    </li>

                  </ul>
                  <ul className="d-lg-flex g-3 pt-1 pb-1 custom-filter-header justify-content-end">
                    {
                      isHavePermissionLockTimeKeeping ? <li>
                        <div>
                          <Button
                            className="btn-primary"
                            color="primary"
                            onClick={() => {
                              const lockIds = data.filter(e => !e.isLocked).map(e => e.id);
                              if (lockIds.length === 0) {
                                toast.warning('Tất cả chấm công đã được khóa')
                                return
                              }
                              onFormCancel();
                              setLockIds(lockIds)
                              toggle('isLock')
                            }}
                          >
                            <Icon name="plus"></Icon>
                            <span>Khóa</span>
                          </Button>
                        </div>
                      </li> : <></>
                    }

                    <li>
                      <div>
                        <Button
                          className="btn-primary"
                          color="primary"
                          onClick={() => {
                            onFormCancel();
                            toggle("add");
                          }}
                        >
                          <Icon name="plus"></Icon>
                          <span>Thêm chấm công</span>
                        </Button>
                      </div>
                    </li>
                    <li>
                      <div>
                        <Button
                          className="btn-primary"
                          color="primary"
                          onClick={() => {
                            onFormCancel();
                            toggle("isAddTimeKeepingInHoliday");
                          }}
                        >
                          <Icon name="plus"></Icon>
                          <span>Thêm chấm công lễ</span>
                        </Button>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <div className="card card-bordered card-preview">
            <div className="card-inner">
              <div
                id="DataTables_Table_1_wrapper"
                className="dataTables_wrapper dt-bootstrap4 no-footer"
              >
                <div className="row justify-between g-2">
                  <div className="col-8 col-sm-6 text-start"></div>
                  <div className="col-5 col-sm-6 text-end">
                    <div className="datatable-filter">
                      <div className="d-flex justify-content-end g-2">
                        <div
                          className="dataTables_length"
                          id="DataTables_Table_1_length"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="my-3">
                  <Table
                    loading={loading}
                    columns={colTable}
                    data={data}
                    notShowPagi={true}
                    headerRow={<tr className="fw-bold">
                      <td colSpan={4}><strong>Tổng cộng</strong></td>
                      <td className="text-right">
                        <NumericFormat
                          displayType={"text"}
                          value={summary?.workingHour || 0}
                          thousandsGroupStyle="thousand"
                          thousandSeparator=","
                          decimalScale={2}
                        />
                      </td>
                      <td className="text-right">
                        <NumericFormat
                          displayType={"text"}
                          value={summary?.extraHour || 0}
                          thousandsGroupStyle="thousand"
                          thousandSeparator=","
                          decimalScale={2}
                        />
                      </td>
                      <td className="text-right">
                        <NumericFormat
                          displayType={"text"}
                          value={summary?.bonusHour || 0}
                          thousandsGroupStyle="thousand"
                          thousandSeparator=","
                          decimalScale={2}
                        />
                      </td>
                      <td className="text-right">
                        <NumericFormat
                          displayType={"text"}
                          value={summary?.calculatedHour || 0}
                          thousandsGroupStyle="thousand"
                          thousandSeparator=","
                          decimalScale={2}
                        />
                      </td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      {isHavePermissionLockTimeKeeping ? <td></td> : <></>}
                      <td></td>
                    </tr>}
                  ></Table>
                </div>
              </div>
            </div>
          </div>
        </Block>

        <Modal isOpen={view.add || view.edit}
          toggle={() => onFormCancel()}
          className="modal-dialog-centered"
          size="lg"
        >
          <ModalBody>
            <a href="#cancel" className="close">
              {" "}
              <Icon
                name="cross-sm"
                onClick={(ev) => {
                  ev.preventDefault();
                  onFormCancel();
                }}
              ></Icon>
            </a>
            <div className="p-2">
              <h5 className="title">{editId ? "Sửa" : "Thêm"}</h5>
              <div className="mt-4">
                <form onSubmit={handleSubmit(onHandleSubmit)}>
                  <Row className="g-3">
                    <Col md="12">
                      <div className="form-group">
                        <label className="form-label" htmlFor="customer">
                          Ngày
                          <span className="text-danger">*</span>
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="date"
                            className="form-control"
                            name="date"
                            max={moment().format("YYYY-MM-DD")}

                            ref={register({
                              required: "Vui lòng nhập thông tin",
                            })}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="date"
                            render={({ message }) => (
                              <span className="invalid">{message}</span>
                            )}
                          />
                        </div>
                      </div>
                    </Col>

                    <Col md="12">
                      <div className="form-group">
                        <label className="form-label" htmlFor="customer">
                          Nhân viên
                          <span className="text-danger">*</span>
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            name="userId"
                            rules={{ required: "Vui lòng nhập thông tin" }}
                            render={({ onChange, value, name, ref }) => (
                              <ReactSelect
                                classNamePrefix="form-control"
                                inputRef={ref}
                                name={name}
                                options={userOptions}
                                value={userOptions.find(
                                  (c) => c.value === value
                                )}
                                onChange={(val) => onChange(val.value)}
                              />
                            )}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="userId"
                            render={({ message }) => (
                              <span className="invalid">{message}</span>
                            )}
                          />
                        </div>
                      </div>
                    </Col>


                    <Col md="12">
                      <div className="form-group">
                        <label className="form-label" htmlFor="customer">
                          Ca làm
                          <span className="text-danger">*</span>
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            name="shift"
                            rules={{ required: "Vui lòng nhập thông tin" }}
                            render={({ onChange, value, name, ref }) => (
                              <ReactSelect
                                classNamePrefix="form-control"
                                inputRef={ref}
                                name={name}
                                options={SHIFT_OPTIONS}
                                value={SHIFT_OPTIONS.find(
                                  (c) => c.value === value
                                )}
                                onChange={(val) => onChange(val.value)}
                              />
                            )}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="shift"
                            render={({ message }) => (
                              <span className="invalid">{message}</span>
                            )}
                          />
                        </div>
                      </div>
                    </Col>

                    <Col md="12">
                      <div className="form-group">
                        <label className="form-label" htmlFor="customer">
                          Số giờ làm
                          <span className="text-danger">*</span>
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            name="totalWorkingHour"
                            rules={{
                              required: "Vui lòng nhập thông tin",
                            }}
                            render={({ onChange, value, ref }) => (
                              <NumericFormat
                                className="form-control"
                                inputRef={ref}
                                type="text"
                                value={value}
                                thousandsGroupStyle="thousand"
                                thousandSeparator=","
                                decimalScale={2}
                                onValueChange={(val) =>
                                  onChange(val.floatValue)
                                }
                                allowNegative={false}
                                min={0}
                                disabled={leaveType || isOffHoliday}
                              />
                            )}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="totalWorkingHour"
                            render={({ message }) => (
                              <span className="invalid">{message}</span>
                            )}
                          />
                        </div>
                      </div>
                    </Col>

                    <Col md="12">
                      <div className="form-group">
                        <label className="form-label" htmlFor="customer">
                          Nghỉ làm
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            name="leaveType"
                            render={({ onChange, value, ref }) => (
                              <RSelect
                                isClearable
                                classNamePrefix="form-control"
                                inputRef={ref}
                                options={TIME_KEEPING_LEAVE_TYPE_OPTIONS}
                                value={TIME_KEEPING_LEAVE_TYPE_OPTIONS.find(
                                  (c) => c.value === value
                                ) || null}
                                isDisabled={isOffHoliday}
                                onChange={(val) => {
                                  if (!val) {
                                    onChange(null)
                                  } else {
                                    onChange(val.value)
                                    setValue('totalWorkingHour', 0)
                                    setValue('isViolationOfCleanliness', false);
                                    setValue('isRunMachine', false);
                                    setValue('isOffHoliday', false);
                                    setValue('isAllowMultiple', false);
                                  }
                                }}
                              />

                            )}
                          />

                        </div>
                      </div>
                    </Col>
                    <Col md="3">
                      <div className="form-group">
                        <label className="form-label" htmlFor="customer">
                          Mất vệ sinh
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            name="isViolationOfCleanliness"
                            render={({ onChange, value, ref }) => (
                              <div className="custom-control custom-switch">
                                <Input
                                  id="isViolationOfCleanlinessSwitch"
                                  className="custom-control-input"
                                  inputRef={ref}
                                  type="checkbox"
                                  checked={!!value}
                                  onClick={() => {
                                    onChange(!value);
                                  }}
                                  disabled={leaveType || isOffHoliday}

                                />
                                <label
                                  className="custom-control-label"
                                  for="isViolationOfCleanlinessSwitch"
                                ></label>
                              </div>
                            )}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col md="3">
                      <div className="form-group">
                        <label className="form-label" htmlFor="customer">
                          Làm nấu dầu
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            name="isOilRefining"
                            render={({ onChange, value, ref }) => (
                              <div className="custom-control custom-switch">
                                <Input
                                  id="isOilRefiningSwitch"
                                  className="custom-control-input"
                                  inputRef={ref}
                                  type="checkbox"
                                  checked={!!value}
                                  onClick={() => {
                                    onChange(!value);
                                    setValue('isRunMachine', false);
                                    setValue('isOffHoliday', false);
                                    setValue('isAllowMultiple', false);

                                  }}
                                  disabled={leaveType || isOffHoliday}
                                />
                                <label
                                  className="custom-control-label"
                                  for="isOilRefiningSwitch"
                                ></label>
                              </div>
                            )}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col md="3">
                      <div className="form-group">
                        <label className="form-label" htmlFor="customer">
                          Chạy máy
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            name="isRunMachine"
                            render={({ onChange, value, ref }) => (
                              <div className="custom-control custom-switch">
                                <Input
                                  id="isRunMachineSwitch"
                                  className="custom-control-input"
                                  inputRef={ref}
                                  type="checkbox"
                                  checked={!!value}
                                  onClick={() => {
                                    onChange(!value);
                                    setValue('isOilRefining', false);
                                    setValue('isOffHoliday', false);
                                    setValue('isAllowMultiple', false);
                                  }}
                                  disabled={leaveType || isOffHoliday}
                                />
                                <label
                                  className="custom-control-label"
                                  for="isRunMachineSwitch"
                                ></label>
                              </div>
                            )}
                          />
                        </div>
                      </div>
                    </Col>


                    <Col md="3">
                      <div className="form-group">
                        <label className="form-label" htmlFor="customer">
                          Nghỉ lễ
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            name="isOffHoliday"
                            render={({ onChange, value, ref }) => (
                              <div className="custom-control custom-switch">
                                <Input
                                  id="isOffHolidaySwitch"
                                  className="custom-control-input"
                                  inputRef={ref}
                                  type="checkbox"
                                  checked={!!value}
                                  onClick={() => {
                                    onChange(!value);
                                    setValue("totalWorkingHour", 8);
                                    setValue('isOilRefining', false);
                                    setValue('isRunMachine', false);
                                    setValue('isAllowMultiple', false);
                                    setValue('isViolationOfCleanliness', false);

                                  }}
                                  disabled={leaveType}
                                />
                                <label
                                  className="custom-control-label"
                                  for="isOffHolidaySwitch"
                                ></label>
                              </div>
                            )}
                          />
                        </div>
                      </div>
                    </Col>

                    {
                      (isRunMachine || isOilRefining) ? <Col md="12">
                        <div className="form-group">
                          <label className="form-label" htmlFor="customer">
                            Tính thêm giờ thưởng chủ nhật/lễ cho nấu dầu và chạy máy
                          </label>
                          <div className="form-control-wrap">
                            <Controller
                              control={control}
                              name="isAllowMultiple"
                              render={({ onChange, value, ref }) => (
                                <div className="custom-control custom-switch">
                                  <Input
                                    id="isAllowMultipleSwitch"
                                    className="custom-control-input"
                                    inputRef={ref}
                                    type="checkbox"
                                    checked={!!value}
                                    onClick={() => {
                                      onChange(!value);
                                    }}
                                    disabled={leaveType || isOffHoliday}
                                  />
                                  <label
                                    className="custom-control-label"
                                    for="isAllowMultipleSwitch"
                                  ></label>
                                </div>
                              )}
                            />
                          </div>
                        </div>
                      </Col> : <></>
                    }
                    <Col size="12">
                      <Button color="primary" type="submit">
                        <span>{editId ? "Sửa" : "Thêm"}</span>
                      </Button>
                    </Col>
                  </Row>
                </form>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal isOpen={view.isDelete} toggle={() => onFormCancel()}>
          <ModalBody className="modal-body-lg text-center">
            <div className="nk-modal">
              <Icon className="nk-modal-icon icon-circle icon-circle-xxl ni ni-cross bg-danger"></Icon>
              <h4 className="nk-modal-title">
                Bạn có chắc muốn xoá chấm công này không ?{" "}
              </h4>
              <div className="nk-modal-action mt-5 d-flex justify-content-around">
                <Button
                  color="light"
                  size="lg"
                  className="btn-mw mr-5"
                  onClick={() => onFormCancel()}
                >
                  Huỷ bỏ
                </Button>
                <Button
                  color="primary"
                  size="lg"
                  className="btn-mw"
                  onClick={(ev) => {
                    ev.preventDefault();
                    deleteOrder(deleteId);
                  }}
                >
                  Xác nhận
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal isOpen={view.isScan} toggle={() => onFormCancel()}>
          <ModalBody className="modal-body-lg">
            <div className="nk-modal">
              {scanData ? <>
                <Row>
                  <Col sm={6}>Nhân viên: {scanData.user?.name}</Col>
                  <Col sm={6}>Ca làm: {SHIFT[scanData.shift]}</Col>
                  {scanData.leaveType ? <Col sm={6}>{TIME_KEEPING_LEAVE_TYPE[scanData.leaveType]}</Col> : ''}
                  <Col sm={6}>Giờ làm: {<NumericFormat
                    displayType={"text"}
                    value={scanData.workingHour || 0}
                    thousandsGroupStyle="thousand"
                    thousandSeparator=","
                    decimalScale={2}
                  />}</Col>
                  <Col sm={6}>Giờ làm thêm: {<NumericFormat
                    displayType={"text"}
                    value={scanData.extraHour || 0}
                    thousandsGroupStyle="thousand"
                    thousandSeparator=","
                    decimalScale={2}
                  />}</Col>
                  <Col sm={6}>Giờ thưởng thêm: {<NumericFormat
                    displayType={"text"}
                    value={scanData.bonusHour || 0}
                    thousandsGroupStyle="thousand"
                    thousandSeparator=","
                    decimalScale={2}
                  />}</Col>
                  <Col sm={6}>Tổng giờ: {<NumericFormat
                    displayType={"text"}
                    value={scanData.calculatedHour || 0}
                    thousandsGroupStyle="thousand"
                    thousandSeparator=","
                    decimalScale={2}
                  />}</Col>
                </Row>
              </> : <></>}
              <div className="d-flex flex-column align-center mt-3">
                <QRCode size={150} value={genScanQRData(scanData)} />
                <Button
                  className={'mt-2'}
                  color="light"
                  size="lg"
                  onClick={() => onFormCancel()}
                >
                  Đóng
                </Button>
              </div>

            </div>
          </ModalBody>
        </Modal>

        <Modal isOpen={view.isLock} toggle={() => onFormCancel()}>
          <ModalBody className="modal-body-lg text-center">
            <div className="nk-modal">
              <Icon className="nk-modal-icon icon-circle icon-circle-xxl ni ni-lock bg-warning"></Icon>
              <h4 className="nk-modal-title">
                Bạn có chắc muốn khoá chấm công này không ?{" "}
              </h4>
              <div className="nk-modal-action mt-5 d-flex justify-content-around">
                <Button
                  color="light"
                  size="lg"
                  className="btn-mw mr-5"
                  onClick={() => onFormCancel()}
                >
                  Huỷ bỏ
                </Button>
                <Button
                  color="primary"
                  size="lg"
                  className="btn-mw"
                  onClick={(ev) => {
                    ev.preventDefault();
                    confirmLocked();
                  }}
                >
                  Xác nhận
                </Button>
              </div>

            </div>
          </ModalBody>
        </Modal>

        <Modal isOpen={view.isAddTimeKeepingInHoliday} toggle={() => onFormCancel()} size="lg">
          <ModalBody>
            <TimeKeepingHoliday onSuccess={resetData} />
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default TimeKeepingManager;
